import moment from 'moment';
import { FoundationCode } from '../customers/index.js';
import { DocumentTypes, documentsWhichIsFoundations, getFoundations, } from '../documents/index.js';
import helpers from '../economy/helpers/index.js';
import { VoucherCategory } from '../economy/vouchers/index.js';
import { Currency } from '../index.js';
import { InteractionEventType } from '../interactions/events/index.js';
import { Direction, InteractionType, } from '../interactions/index.js';
import { TemplateTag } from '../templates/index.js';
import { UpdateType } from '../updates/index.js';
import { DebtRegistrationState, Registry, RegistryType, } from './index.js';
// Generic Danish debt-registration rules shared by all registries (RKI, Debitorregistret).
// Registry-specific behaviour is described by REGISTRY_CAPABILITIES and injected into the
// shared evaluator, so there is a single source of truth for the eligibility logic.
// Balance thresholds for the open and closed registries. They are shared by all
// Danish registries: DBR only has an open registry and uses the same open threshold.
export var RequiredAmount;
(function (RequiredAmount) {
    RequiredAmount[RequiredAmount["OPEN"] = 100000] = "OPEN";
    RequiredAmount[RequiredAmount["CLOSED"] = 20000] = "CLOSED";
})(RequiredAmount || (RequiredAmount = {}));
export var MaximumAgeInYearsOfRegistration;
(function (MaximumAgeInYearsOfRegistration) {
    MaximumAgeInYearsOfRegistration[MaximumAgeInYearsOfRegistration["CLOSED"] = 2] = "CLOSED";
    MaximumAgeInYearsOfRegistration[MaximumAgeInYearsOfRegistration["OPEN"] = 5] = "OPEN";
})(MaximumAgeInYearsOfRegistration || (MaximumAgeInYearsOfRegistration = {}));
export var DebtRegistrationError;
(function (DebtRegistrationError) {
    DebtRegistrationError["AlreadyRegistered"] = "AlreadyRegistered";
    DebtRegistrationError["MissingWarning"] = "MissingWarning";
    DebtRegistrationError["WrongCurrency"] = "WrongCurrency";
    DebtRegistrationError["WarningLetterWasReturned"] = "WarningLetterWasReturned";
    DebtRegistrationError["EmailWarningWasNeverDelivered"] = "EmailWarningWasNeverDelivered";
    DebtRegistrationError["WarningIsOlderThanOneYear"] = "WarningIsOlderThanOneYear";
    DebtRegistrationError["FoundationCouldNotBeMapped"] = "FoundationCouldNotBeMapped";
    DebtRegistrationError["FoundationDateCouldNotFound"] = "FoundationDateCouldNotFound";
    DebtRegistrationError["UnresolvedOpposition"] = "UnresolvedOpposition";
    DebtRegistrationError["OppositionFound"] = "OppositionFound";
    DebtRegistrationError["NoRegistryFound"] = "NoRegistryFound";
    DebtRegistrationError["FoundationAmountCouldNotBeFound"] = "FoundationAmountCouldNotBeFound";
    DebtRegistrationError["FoundationAmountTooSmall"] = "FoundationAmountTooSmall";
    DebtRegistrationError["NameTooLong"] = "NameTooLong";
    DebtRegistrationError["OldestRegistrationIsTooOld"] = "OldestRegistrationIsTooOld";
    DebtRegistrationError["FoundationFileMissing"] = "FoundationFileMissing";
    DebtRegistrationError["FoundationMissing"] = "FoundationMissing";
    DebtRegistrationError["BalanceTooSmallForOpen"] = "BalanceTooSmallForOpen";
})(DebtRegistrationError || (DebtRegistrationError = {}));
// RKI maps our foundation document type to its FoundationCode enum.
export const foundationCodeMapper = (foundation) => {
    if (!foundation) {
        return '-1';
    }
    switch (foundation.type) {
        case DocumentTypes.DOM:
            return FoundationCode.dom;
        case DocumentTypes.PAATEGNING:
            return FoundationCode.betalingspåkrav2påtegning;
        case DocumentTypes.FORLIG:
            return FoundationCode.forlig;
        case DocumentTypes.SKYLDERKLAERING:
            return FoundationCode.skylderklæring;
        default:
            return '-1';
    }
};
export const REGISTRY_CAPABILITIES = {
    [Registry.RKI]: {
        registry: Registry.RKI,
        hasClosedRegistry: true,
        foundationCodeMapper,
        maxOpenAgeYears: MaximumAgeInYearsOfRegistration.OPEN,
        supportsAdvokatindestaaelse: true,
    },
    [Registry.DEBITOR_REGISTRET]: {
        registry: Registry.DEBITOR_REGISTRET,
        hasClosedRegistry: false,
        // Eligibility yields our internal FoundationCode; the DBR-specific numeric
        // reason code is applied at the API boundary in the provider module.
        foundationCodeMapper,
        maxOpenAgeYears: MaximumAgeInYearsOfRegistration.OPEN,
        supportsAdvokatindestaaelse: false,
    },
};
const isNameTooLong = (debtorUsers) => {
    const debtorToBeRegistred = debtorUsers.find((debtor) => debtor.relation === 'debtor');
    return !!(debtorToBeRegistred && (debtorToBeRegistred.details.name || '').length >= 60);
};
// Whether the case is already actively registered in the given registry.
const isAlreadyRegistered = (debtRegistrations, registry) => {
    return debtRegistrations.some((reg) => reg.state === DebtRegistrationState.ACTIVE && reg.registry === registry);
};
const byOldestCreatedAt = (a, b) => {
    if (moment(a.createdAt).isBefore(moment(b.createdAt))) {
        return -1;
    }
    else if (moment(a.createdAt).isAfter(moment(b.createdAt))) {
        return 1;
    }
    return 0;
};
// Decide which registry type (OPEN/CLOSED) the case qualifies for. Only used for
// registries whose capabilities declare a closed registry (currently RKI is the only
// one). autoOpen requests the OPEN registry when eligible.
const getOpenOrClosedRegistry = (caseVouchers, foundations, autoOpen) => {
    if (autoOpen &&
        foundations.length > 0 &&
        -helpers.overview.balance(caseVouchers) >= RequiredAmount.OPEN) {
        return RegistryType.OPEN;
    }
    if (-helpers.overview.balanceCategory(caseVouchers, VoucherCategory.PRINCIPAL) >= RequiredAmount.CLOSED &&
        -helpers.overview.balance(caseVouchers) >= RequiredAmount.CLOSED) {
        return RegistryType.CLOSED;
    }
    return null;
};
const getTargetRegistryType = (caps, caseVouchers, foundations, options) => {
    // An explicit open-registry request (the caseworker ticking "Med fundament") always
    // evaluates the open path, so every registry reports the same open-path problems.
    // Without this, RKI silently falls back to its closed registry and reports closed-path
    // errors (e.g. MissingWarning) while an open-only registry reports NoRegistryFound.
    if (options.requireOpen) {
        return RegistryType.OPEN;
    }
    if (caps.hasClosedRegistry) {
        return getOpenOrClosedRegistry(caseVouchers, foundations, options.autoOpen);
    }
    // OPEN-only registry (e.g. Debitorregistret): requires a foundation and the open threshold.
    if (foundations.length > 0 &&
        -helpers.overview.balance(caseVouchers) >= RequiredAmount.OPEN) {
        return RegistryType.OPEN;
    }
    return null;
};
export const getOpenRegistryErrorsAndDetails = (foundations, debtRegistrations, options) => {
    const errors = [];
    const foundation = foundations[0];
    const foundationCode = options.foundationCodeMapper(foundation);
    const oldestRegistration = debtRegistrations && debtRegistrations.length > 0
        ? [...debtRegistrations].sort(byOldestCreatedAt)[0]
        : null;
    const foundationsDate = oldestRegistration &&
        oldestRegistration.state !== DebtRegistrationState.ACTIVE
        ? oldestRegistration.claim.foundationDate
        : documentsWhichIsFoundations.signingNotRequired.includes(foundation.type)
            ? foundation.date
            : documentsWhichIsFoundations.signingRequired.includes(foundation.type)
                ? foundation.signedAt
                : null;
    const amount = foundation.meta.amount;
    if (foundationCode === '-1') {
        errors.push(DebtRegistrationError.FoundationCouldNotBeMapped);
    }
    if (!foundationsDate) {
        errors.push(DebtRegistrationError.FoundationDateCouldNotFound);
    }
    if (oldestRegistration &&
        moment(oldestRegistration.claim.foundationDate).isBefore(moment().subtract(options.maxAgeYears, 'years').add(1, 'month'))) {
        errors.push(DebtRegistrationError.OldestRegistrationIsTooOld);
    }
    if (!amount) {
        errors.push(DebtRegistrationError.FoundationAmountCouldNotBeFound);
    }
    else if (amount < RequiredAmount.OPEN) {
        errors.push(DebtRegistrationError.FoundationAmountTooSmall);
    }
    return {
        errors,
        foundationCode,
        foundationDate: foundationsDate || undefined,
        foundationAmount: amount || undefined,
        foundationFileId: foundation.signedFile
            ? foundation.signedFile.fileId || undefined
            : foundation.file
                ? foundation.file.fileId || undefined
                : undefined,
        oldestRegistration: oldestRegistration || undefined,
    };
};
export const getDebtRegistrationWarningErrors = (interactions, updates, templates) => {
    const sixMonthsAgo = moment().subtract(6, 'months').add(1, 'days');
    const fourteenDaysAgo = moment().subtract(14, 'days');
    const errors = [];
    const warningTemplateIds = templates
        .filter((template) => {
        return template.tags.some((tag) => tag.tag === TemplateTag.DebtRegistrationWarning);
    })
        .map((template) => template.templateId);
    const alreadyWarnedUpdates = updates.filter((u) => u.updateTypeId === UpdateType.DEBTREGISTRATIONWARNED &&
        sixMonthsAgo.isBefore(moment(u.pausedAt)) &&
        fourteenDaysAgo.isAfter(moment(u.pausedAt)));
    if (alreadyWarnedUpdates.length) {
        return errors;
    }
    // Check warning letter is max 6 months old
    const outgoingInteractions = interactions.filter((i) => i.direction === Direction.OUTGOING);
    const warningInteraction = outgoingInteractions.filter((interaction) => {
        if (interaction.templateId) {
            return warningTemplateIds.includes(interaction.templateId);
        }
        return false;
    });
    if (!warningInteraction.length) {
        errors.push(DebtRegistrationError.MissingWarning);
    }
    // Filter returned letters
    const notReturnedWarnings = warningInteraction.filter((interaction) => {
        if (interaction.type === InteractionType.Letter) {
            if (interaction.events.some((event) => event.type === InteractionEventType.Failed)) {
                return false;
            }
        }
        return true;
    });
    if (!notReturnedWarnings.length && warningInteraction.length) {
        errors.push(DebtRegistrationError.WarningLetterWasReturned);
    }
    const deliveredWarnings = notReturnedWarnings.filter((interaction) => {
        if (interaction.type === InteractionType.Email) {
            const deliveredEvents = interaction.events.filter((event) => event.type === InteractionEventType.Delivered);
            if (deliveredEvents.length) {
                return true;
            }
            return false;
        }
        return true;
    });
    if (!deliveredWarnings.length && notReturnedWarnings.length) {
        errors.push(DebtRegistrationError.EmailWarningWasNeverDelivered);
    }
    const currentWarnings = deliveredWarnings.filter((interaction) => {
        if (sixMonthsAgo.isBefore(moment(interaction.createdAt))) {
            return true;
        }
        return false;
    });
    if (!currentWarnings.length && deliveredWarnings.length) {
        errors.push(DebtRegistrationError.WarningIsOlderThanOneYear);
    }
    return errors;
};
export const getOppositionErrors = (interactions, updates, autoIgnoreUnresolvedOppositions) => {
    const errors = [];
    // Check for any old incoming interaction and block if any is found
    const incomingInteractions = interactions.filter((interaction) => interaction.type === InteractionType.Phone ||
        (interaction.direction === Direction.INCOMING &&
            ![InteractionType.Note, InteractionType.ClientNote].includes(interaction.type)));
    let unresolvedOpposition = false;
    const oppositions = incomingInteractions.filter((interaction) => {
        const interactionUpdates = updates.filter((update) => update.refId === interaction.id &&
            [
                UpdateType.OPPOSITION.toString(),
                UpdateType.NOTOPPOSITION.toString(),
            ].includes(update.updateTypeId));
        if (interactionUpdates.length) {
            interactionUpdates.sort((a, b) => {
                // returns newest first
                if (moment(a.createdAt) > moment(b.createdAt)) {
                    return -1;
                }
                else if (moment(a.createdAt) < moment(b.createdAt)) {
                    return 1;
                }
                return 0;
            });
            if (interactionUpdates[0]?.updateTypeId === UpdateType.OPPOSITION) {
                return true;
            }
        }
        else {
            unresolvedOpposition = true;
        }
        return false;
    });
    const unmappedOppositions = updates.filter((update) => {
        if (update.updateTypeId === UpdateType.OPPOSITION.toString()) {
            return !update.refId;
        }
        return false;
    });
    if (!autoIgnoreUnresolvedOppositions && unresolvedOpposition) {
        errors.push(DebtRegistrationError.UnresolvedOpposition);
    }
    if (oppositions.length || unmappedOppositions.length) {
        errors.push(DebtRegistrationError.OppositionFound);
    }
    return errors;
};
const getClosedRegistryErrors = (interactions, updates, templates, autoIgnoreUnresolvedOppositions) => {
    const errors = [];
    errors.push(...getDebtRegistrationWarningErrors(interactions, updates, templates));
    errors.push(...getOppositionErrors(interactions, updates, autoIgnoreUnresolvedOppositions));
    return errors;
};
// Unified eligibility check for any debt registry. The per-registry differences
// (closed registry support, foundation-code mapping, warning requirements) are read
// from REGISTRY_CAPABILITIES so the rules live in exactly one place.
export const canRegisterInRegistry = (registry, kase, debtorUsers, caseVouchers, debtRegistrations, interactions, documents, updates, templates, options) => {
    const caps = REGISTRY_CAPABILITIES[registry];
    // Only consider registrations belonging to the registry being evaluated, so a
    // registration in one registry never blocks eligibility for another.
    const registryRegistrations = debtRegistrations.filter((reg) => reg.registry === registry);
    const errors = [];
    let foundationCode;
    let foundationDate;
    let foundationAmount;
    let foundationFileId;
    let oldestRegistration;
    if (isAlreadyRegistered(registryRegistrations, registry)) {
        errors.push(DebtRegistrationError.AlreadyRegistered);
    }
    if (isNameTooLong(debtorUsers)) {
        errors.push(DebtRegistrationError.NameTooLong);
    }
    if (kase.currency !== Currency.DKK) {
        errors.push(DebtRegistrationError.WrongCurrency);
    }
    const foundations = getFoundations(documents);
    const registryType = getTargetRegistryType(caps, caseVouchers, foundations, {
        autoOpen: options?.autoOpen || false,
        requireOpen: options?.requireOpen || false,
    });
    if (registryType === RegistryType.OPEN) {
        // The open path is only reachable without a foundation when it was demanded via
        // requireOpen; tell the caseworker that is what is missing rather than evaluating
        // foundation details that do not exist.
        if (foundations.length === 0) {
            errors.push(DebtRegistrationError.FoundationMissing);
        }
        else {
            const open = getOpenRegistryErrorsAndDetails(foundations, registryRegistrations, {
                foundationCodeMapper: caps.foundationCodeMapper,
                maxAgeYears: caps.maxOpenAgeYears,
            });
            errors.push(...open.errors);
            foundationCode = open.foundationCode;
            foundationDate = open.foundationDate;
            foundationAmount = open.foundationAmount;
            foundationFileId = open.foundationFileId;
            oldestRegistration = open.oldestRegistration;
            // For manglerAdvokatindestaaelse tenants the foundationCode is always '0', so the
            // attached file is the only thing that makes a registration "open" at RKI. Only
            // registries that support lawyer approval enforce this; block if no file exists.
            if (caps.supportsAdvokatindestaaelse &&
                options?.manglerAdvokatindestaaelse &&
                foundationFileId == null) {
                errors.push(DebtRegistrationError.FoundationFileMissing);
            }
        }
        // Only reachable via requireOpen — the other paths into OPEN already require the
        // balance to clear the threshold.
        if (-helpers.overview.balance(caseVouchers) < RequiredAmount.OPEN) {
            errors.push(DebtRegistrationError.BalanceTooSmallForOpen);
        }
    }
    else if (registryType === RegistryType.CLOSED) {
        errors.push(...getClosedRegistryErrors(interactions, updates, templates, options?.autoIgnoreUnresolvedOppositions));
    }
    else {
        errors.push(DebtRegistrationError.NoRegistryFound);
    }
    return {
        ready: errors.length === 0,
        errors,
        details: {
            registry: registryType,
            foundationCode,
            foundationDate,
            foundationAmount,
            foundationFileId,
            oldestRegistration,
        },
    };
};
export const getDateOfLatestWarning = (interactions, updates, templates) => {
    const sixMonthsAgo = moment().subtract(6, 'months').add(1, 'days');
    const fourteenDaysAgo = moment().subtract(14, 'days');
    const warningTemplateIds = templates
        .filter((template) => {
        return template.tags.some((tag) => tag.tag === TemplateTag.DebtRegistrationWarning);
    })
        .map((template) => template.templateId);
    // Check warning letter is max 1 year old
    const outgoingInteractions = interactions.filter((i) => i.direction === Direction.OUTGOING);
    const warningInteraction = outgoingInteractions.filter((interaction) => {
        if (interaction.templateId) {
            return warningTemplateIds.includes(interaction.templateId);
        }
        return false;
    });
    // Filter returned letters
    const notReturnedWarnings = warningInteraction.filter((interaction) => {
        if (interaction.type === InteractionType.Letter) {
            if (interaction.events.length) {
                return false;
            }
        }
        return true;
    });
    const deliveredWarnings = notReturnedWarnings.filter((interaction) => {
        if (interaction.type === InteractionType.Email) {
            const deliveredEvents = interaction.events.filter((event) => event.type === 'delivered');
            if (deliveredEvents.length) {
                return true;
            }
            return false;
        }
        return true;
    });
    const currentWarnings = deliveredWarnings.filter((interaction) => {
        const fourteenDaysAgo = moment().subtract(14, 'days');
        if (fourteenDaysAgo.isBefore(moment(interaction.createdAt))) {
            return false;
        }
        return true;
    });
    const newestWarning = currentWarnings.sort((a, b) => {
        // returns newest first
        if (moment(a.createdAt) > moment(b.createdAt)) {
            return -1;
        }
        else if (moment(a.createdAt) < moment(b.createdAt)) {
            return 1;
        }
        return 0;
    })[0];
    const warnedUpdates = updates.filter((u) => u.updateTypeId === UpdateType.DEBTREGISTRATIONWARNED &&
        sixMonthsAgo.isBefore(moment(u.pausedAt)) &&
        fourteenDaysAgo.isAfter(moment(u.pausedAt)));
    const newestWarningUpdate = warnedUpdates.sort((a, b) => {
        // returns newest first
        if (moment(a.createdAt) > moment(b.createdAt)) {
            return -1;
        }
        else if (moment(a.createdAt) < moment(b.createdAt)) {
            return 1;
        }
        return 0;
    })[0];
    if (newestWarning && newestWarningUpdate) {
        if (moment(newestWarning.createdAt).isAfter(moment(newestWarningUpdate.pausedAt))) {
            return moment(newestWarning.createdAt).format();
        }
        return moment(newestWarningUpdate.pausedAt).format();
    }
    else if (newestWarningUpdate) {
        return moment(newestWarningUpdate.pausedAt).format();
    }
    return newestWarning ? moment(newestWarning.createdAt).format() : undefined;
};
export const getPersonNameForDebtRegister = (debtorUser) => {
    let personName = debtorUser.details.name;
    if (debtorUser.details.virksomhedsform &&
        ['Personligt ejet Mindre Virksomhed', 'Enkeltmandsvirksomhed'].includes(debtorUser.details.virksomhedsform)) {
        personName =
            debtorUser.details.companyRelations &&
                debtorUser.details.companyRelations[0]?.name;
    }
    return personName || undefined;
};
export const isAutoDebtRegisterEnabled = (tenant, creditor) => {
    let autoDebtRegister = false;
    if (creditor.settings.autoDebtRegister !== undefined) {
        autoDebtRegister = creditor.settings.autoDebtRegister;
    }
    else {
        if (tenant.settings?.autoDebtRegister != null) {
            autoDebtRegister = tenant.settings.autoDebtRegister;
        }
    }
    return autoDebtRegister;
};
export const isAutoRegisterInOpenRegistryEnabled = (tenant, creditor) => {
    let autoRegisterInOpenRegistry = false;
    if (creditor.settings.autoRegisterInOpenRegistry !== undefined) {
        autoRegisterInOpenRegistry = creditor.settings.autoRegisterInOpenRegistry;
    }
    else {
        if (tenant.settings?.autoRegisterInOpenRegistry != null) {
            autoRegisterInOpenRegistry = tenant.settings.autoRegisterInOpenRegistry;
        }
    }
    return autoRegisterInOpenRegistry;
};
export const isAutoIgnoreUnresolvedOppositionsEnabled = (tenant, creditor) => {
    let autoIgnoreUnresolvedOppositions = false;
    if (creditor.settings.autoIgnoreUnresolvedOppositions !== undefined) {
        autoIgnoreUnresolvedOppositions =
            creditor.settings.autoIgnoreUnresolvedOppositions;
    }
    else {
        if (tenant.settings?.autoIgnoreUnresolvedOppositions != null) {
            autoIgnoreUnresolvedOppositions =
                tenant.settings.autoIgnoreUnresolvedOppositions;
        }
    }
    return autoIgnoreUnresolvedOppositions;
};
//# sourceMappingURL=denmark.js.map